import React, { useMemo } from 'react';
import { Flex, Grid, Text } from 'onefaceui-react-components';
import PropTypes from 'prop-types';

import { useRoute } from 'react/queries/Route';
import {
  getResourceTo
} from 'react/utils/utils';
import { getRouteStatusLabel } from 'react/utils/routeUtils';
import queryClient from 'react/services/queryClient';
import Loading from 'react/components/Loading';
import progressNumbersHelper from 'react/api/dto/ProgressNumbers';

import TimesSection from '../components/TimesSection';
import DistancesSection from '../components/DistancesSection';
import CostsSection from '../components/CostsSection';
import ProgressBar from '../../RoutesList/list/ProgressBar';
import StatusIcon from '../../RoutesList/list/StatusIcon';
import RouteAssignments from './RouteAssignments';
import { Container } from './styles';
import RouteActionsMenu from './RouteActionsMenu';

function RouteInfo({ routeId, routeView }) {
  const { data: route = {}, isFetching } = useRoute(routeId);
  const driverAssignments = useMemo(() => route.driverAssignments, [route]);
  const equipmentAssignments = useMemo(
    () => route.equipmentAssignments,
    [route]
  );
  const tagAssignments = useMemo(() => route.tagAssignments, [route]);

  const progressNumbers = useMemo(
    () =>
      progressNumbersHelper({
        route: routeView.route,
        stopStats: routeView.stopStats
      }),
    [route]
  );

  const title = useMemo(() => {
    if (!route.key && !route.date) return '';

    const dateFormatted = Monet.Format.Date.formatToLocale(route.date, {
      dayMonthOnly: true
    });

    return `${String(route.key).toUpperCase()} (${dateFormatted})`;
  }, [route]);

  const stopsServiced = useMemo(() => {
    if (!route.key) return '';

    const { totalDone, total } = progressNumbers;

    return `${totalDone}/${total}`;
  }, [progressNumbers]);

  function onClickOnEditDrivers() {
    new Greenmile.Route.AssignDriver({
      routeId: route.id,
      modalContentSelector: '#RouteMonitor .popupFormContainer',
      scopeBlock: '#RouteMonitor',
      selector: '#RouteMonitor',
      successCallback: () => {
        queryClient.invalidateQueries(['route', route.id]);
      },
      afterRemoveItems: () => {
        queryClient.invalidateQueries(['route', route.id]);
      }
    }).showAssignDriver();
  }

  function onClickOnEditEquipments() {
    new Greenmile.Route.AssignEquipment({
      routeId: route.id,
      modalContentSelector: '#RouteMonitor .popupFormContainer',
      scopeBlock: '#RouteMonitor',
      selector: '#RouteMonitor',
      successCallback: () => {
        queryClient.invalidateQueries(['route', route.id]);
      },
      afterRemoveItems: () => {
        queryClient.invalidateQueries(['route', route.id]);
      }
    }).showAssignEquipment();
  }

  function onClickOnEditTags() {
    new Greenmile.Route.AssignTag({
      routeId: route.id,
      modalContentSelector: '#RouteMonitor .popupFormContainer',
      scopeBlock: '#RouteMonitor',
      selector: '#RouteMonitor',
      successCallback: () => {
        queryClient.invalidateQueries(['route', route.id]);
      },
      afterRemoveItems: () => {
        queryClient.invalidateQueries(['route', route.id]);
      }
    }).showAssignTag();
  }

  function renderRouteTitle() {
    return (
      <Grid templateColumns="6fr 1fr 1fr" alignItems="flex-start" gap={8}>
        <Flex flexDirection="column">
          <Text size="sm" weight="bold" dataTestId="route-title">
            {title}
          </Text>
          <Text size="xs" dataTestId="route-description">
            {route.description}
          </Text>
        </Flex>
        <Flex data-testid="route-organization" flexDirection="column">
          <Text weight="bold" size="xs">
            {getResourceTo('route.organization')}
          </Text>
          <Text size="xs" overflow="ellipsis" dataTestId="route-organization">
            {route.organization?.description}
          </Text>
        </Flex>
        <Flex alignItems="center" justifyContent="flex-end">
          <RouteActionsMenu
            route={route}
            equipmentAssignments={equipmentAssignments}
          />
        </Flex>
      </Grid>
    );
  }

  function renderAssignments() {
    return (
      <RouteAssignments
        driverAssignments={driverAssignments}
        equipmentAssignments={equipmentAssignments}
        tagAssignments={tagAssignments}
        onClickOnEditDrivers={onClickOnEditDrivers}
        onClickOnEditEquipments={onClickOnEditEquipments}
        onClickOnEditTags={onClickOnEditTags}
      />
    );
  }

  function renderRouteInfo() {
    return (
      <Flex className="routeMonitorRouteItem" flexDirection="column" gap={8}>
        <Grid templateColumns="1fr 1fr" gap={8} alignItems="flex-end">
          <Flex flexDirection="column" gap={6}>
            <Flex gap={2} alignItems="center">
              <StatusIcon routeView={{ route }} />
              <Text size="xs" dataTestId="route-status">
                {getRouteStatusLabel(route.status)}
              </Text>
            </Flex>
            <Flex flexDirection="column" gap={4}>
              <ProgressBar
                testid={`progress-bar-route-${route.id}`}
                progressNumbers={progressNumbers}
                onMouseOver={() => null}
                onMouseOut={() => null}
                onMouseMove={() => null}
                routeId={route.id}
                isLoading={isFetching}
              />
              <Flex justifyContent="space-between" alignItems="center">
                <Text weight="bold" size="xs">
                  {getResourceTo('RouteMonitor.sidebar.StopsServiced')}
                </Text>
                <Text weight="bold" size="xs" dataTestId="route-serviced">
                  {stopsServiced}
                </Text>
              </Flex>
            </Flex>
          </Flex>
          <Grid className="locations" templateColumns="1fr 1fr" gap={4}>
            <Flex data-testid="route-origin" flexDirection="column">
              <Text weight="bold" size="xs">
                {getResourceTo('route.originLocationID')}
              </Text>
              <Text size="xs" dataTestId="route-origin">
                {route.origin?.description}
              </Text>
            </Flex>
            <Flex data-testid="route-destination" flexDirection="column">
              <Text weight="bold" size="xs">
                {getResourceTo('route.destionationLocationID')}
              </Text>
              <Text
                size="xs"
                overflow="ellipsis"
                dataTestId="route-destination"
              >
                {route.destination?.description}
              </Text>
            </Flex>
          </Grid>
        </Grid>
        {renderAssignments()}
        <TimesSection route={route} />
        <DistancesSection route={route} />
        <CostsSection route={route} />
      </Flex>
    );
  }

  if (isFetching) {
    return (
      <Flex
        alignItems="center"
        justifyContent="center"
        style={{ height: '100%' }}
      >
        <Loading />
      </Flex>
    );
  }

  return (
    <Container
      flexDirection="column"
      gap={6}
      dataTestId={`panel-route-${route.key}`}
    >
      {renderRouteTitle()}
      {renderRouteInfo()}
    </Container>
  );
}

RouteInfo.propTypes = {
  routeId: PropTypes.number.isRequired,
  routeView: PropTypes.object.isRequired
};

export default RouteInfo;
